/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { IHeaderViewProps } from '@msdyn365-commerce-modules/header/src/modules/header/./header';
/* VSI-Customization - Start */
import {
    addThrottledEvent,
    Button,
    getPayloadObject,
    getTelemetryAttributes,
    getTelemetryObject,
    Modal,
    ModalBody,
    ModalHeader,
    Module,
    Node,
    onTelemetryClick,
    TelemetryConstant
} from '@msdyn365-commerce-modules/utilities';
/* VSI-Customization - End */
import * as MsDyn365 from '@msdyn365-commerce/core';
/* VSI-Customization - Start */
import { Customer } from '@msdyn365-commerce/retail-proxy';
import classnames from 'classnames';
import get from 'lodash/get';
import * as React from 'react';
import { NavMenuStateInput } from '../../../data-actions/navigation-menu-state-data-action';
import _getViewPort from '../../../Utilities/get-view-port';
import { navLinkKeywordRemoval } from '../../../Utilities/plp-url-utils';
import { HeaderPreferredStore } from '../custom-components/header-preferred-store';
import Logo from '../custom-components/logo';
import { IHeaderProps, IHeaderResources, IUtilityLinksData } from '../definition-extensions/header.ext.props.autogenerated';
import { IMfrmExtendedNavMenuData, IMfrmExtendedStoreSelectorData, ISecondaryNavItemsData } from './header.data';
import { isExternalLink } from '../../../Utilities/isExternalLink';
/* VSI-Customization - End */


// tslint:disable-next-line: max-func-body-length
const headerView: React.FC<IHeaderViewProps &
    IHeaderResources &
    IHeaderProps<IMfrmExtendedNavMenuData & IMfrmExtendedStoreSelectorData>> = props => {
        const [isPageScroll, setIsPageScroll] = React.useState<boolean>(false);
        const [isMobile, setIsMobile] = React.useState<boolean>(false);
        const [isTablet, setIsTablet] = React.useState<boolean>(false);
        const [isOpen, setIsOpen] = React.useState<boolean>(false);
        const { HeaderTag, HeaderContainer, HeaderTopBarContainer, MobileMenuLinksContainer, config, context } = props;
        // change tag to div so there isnt nested <headers>
        const updatedHeaderTag = {
            moduleProps: HeaderTag.moduleProps,
            className: classnames(HeaderTag.className),
            tag: 'div'
        };

        {
            /* VSI-Customization - Start - destructured and filtered data   */
        }
        const mfrmMenuBar =
            (props.slots && props.slots.extendedMenuBar && props.slots.extendedMenuBar.length && props.slots.extendedMenuBar) || props.menuBar;
        const mfrmSearch =
            (props.slots && props.slots.mfrmSearchSlot && props.slots.mfrmSearchSlot.length && props.slots.mfrmSearchSlot) || props.search;
        const storeSelector = props.slots &&
            props.slots.mfrmStoreSelector &&
            props.slots.mfrmStoreSelector.length &&
            props.data.storeSelectorStateManager && <HeaderPreferredStore {...props} />;
        const mfrmNotfications =
            props.slots && props.slots.mfrmNotifications && props.slots.mfrmNotifications.length && props.slots.mfrmNotifications;
        const mfrmCartIcon = props.slots && props.slots.mfrmCartIcon && props.slots.mfrmCartIcon.length && props.slots.mfrmCartIcon;
        const { secondaryLogoLink, secondaryLogoImage, utilityLinks, isHeaderSticky } = config;
        const desktopUtilityLinks = utilityLinks && utilityLinks.filter(link => link.showUnderUtilityDesktop === true);
        const tabUtilityLinks = utilityLinks && utilityLinks.filter(link => link.showUnderUtilityTab === true);
        const hamburgerUtilityLinks = utilityLinks && utilityLinks.filter(link => link.showUnderHamburger === true);
        const hamburgerTabUtilityLinks =
            utilityLinks && utilityLinks.filter(link => link.showUnderHamburger === true && link.hideFromTab !== true);
        const navMenuParentActive = props.data.navMenuState.result?.isMenuHasParent;
        const secondaryNavConfig = get(mfrmMenuBar[0], 'props.config') || [];
        const { secondaryNavItems, menuItemsLimitDesktop, menuItemsLimitMobile } = secondaryNavConfig;
        const menuItemLimit = isMobile || isTablet ? menuItemsLimitMobile : menuItemsLimitDesktop;
        const secondaryNavLimit = menuItemLimit !== undefined ? menuItemLimit : secondaryNavItems ? secondaryNavItems.length : 0 || 0;
        const customer = props.data.accountInformation?.result;
        const telemetryContent = getTelemetryObject(props.context.request.telemetryPageName!, props.friendlyName, props.telemetry);
        const stickyNavigation = () => {
            const InitialTopBarSize =
                (MsDyn365.msdyn365Commerce.isBrowser &&
                    document
                        .getElementsByClassName('ms-header__topbar')
                        .item(0)
                        ?.getBoundingClientRect().height) ||
                0;
            const [visibleHeight, setVisibleHeight] = React.useState<number>(0);
            const [topBarVisibleHeight, setTopBarVisibleHeight] = React.useState<number>(0);
            const scrollValue = 0;
            const scrollBarWidth = MsDyn365.msdyn365Commerce.isBrowser && window.innerWidth - document.body.offsetWidth;
            const windowWidth = MsDyn365.msdyn365Commerce.isBrowser && document.documentElement.clientWidth;
            if (MsDyn365.msdyn365Commerce.isBrowser) {
                if (props.mobileMenuCollapsed !== true) {
                    document.body.style.setProperty('overflow', 'hidden');
                    document.documentElement.style.setProperty('overflow', 'hidden');
                } else {
                    document.body.style.removeProperty('overflow');
                    document.documentElement.style.removeProperty('overflow');
                }
            }

            React.useEffect(() => {
                MsDyn365.msdyn365Commerce.isBrowser &&
                    addThrottledEvent(window, 'resize', () => updateViewPort(context, setIsOpen, setIsMobile, setIsTablet));
                updateViewPort(context, setIsOpen, setIsMobile, setIsTablet);
            });

            // for viewport innerHeight
            const InitialHeight = MsDyn365.msdyn365Commerce.isBrowser && document.documentElement.clientHeight;
            const [androidBannerViewHeight, setAndroidBannerViewHeight] = React.useState<number>(87);
            const wrapperElement = document.querySelector('.ms-header__container__wrapper');
            const secondaryElement = document.querySelector('.ms-header__secondary');
            const bannerElement = document.querySelector<HTMLElement>('.mfrm-android-banner-container');

            if (bannerElement) {
                const bannerStyle = getComputedStyle(bannerElement);
                if (bannerStyle.display === 'flex') {
                    if (androidBannerViewHeight === 0) {
                        wrapperElement?.classList.add('ms-header__container__wrapper--lock');
                        secondaryElement?.classList.add('ms-header__secondary--lock');
                    } else if (window.pageYOffset > 87) {
                        wrapperElement?.classList.add('ms-header__container__wrapper--lock');
                        secondaryElement?.classList.add('ms-header__secondary--lock');
                    } else if (androidBannerViewHeight > 0 && androidBannerViewHeight <= 87) {
                        wrapperElement?.classList.remove('ms-header__container__wrapper--lock');
                        secondaryElement?.classList.remove('ms-header__secondary--lock');
                    }
                } else {
                    wrapperElement?.classList.add('ms-header__container__wrapper--lock');
                    secondaryElement?.classList.add('ms-header__secondary--lock');
                }
            }

            // storing viewheight into css variable
            if (MsDyn365.msdyn365Commerce.isBrowser) {
                document.documentElement.style.setProperty('--windowSize', `${InitialHeight}px`);
                document.documentElement.style.setProperty('--topBarSize', `${topBarVisibleHeight}px`);
                document.documentElement.style.setProperty('--scrollbarWidth', `${scrollBarWidth}px`);
                document.documentElement.style.setProperty('--windowWidth', `${windowWidth}px`);
                document.documentElement.style.setProperty('--initialTopbarSize', `${InitialTopBarSize}px`);
                document.documentElement.style.setProperty('--storeLocatorSize', `${visibleHeight}px`);
                document.documentElement.style.setProperty('--pageYOffset', `${androidBannerViewHeight}px`);
                window.addEventListener('resize', () => {
                    const vh = document.documentElement.clientHeight;
                    const scrollBarWidthUpdate = window.innerWidth - document.body.offsetWidth;
                    const windowWidthUpdate = document.documentElement.clientWidth;
                    document.documentElement.style.setProperty('--windowSize', `${vh}px`);
                    document.documentElement.style.setProperty('--scrollbarWidth', `${scrollBarWidthUpdate}px`);
                    document.documentElement.style.setProperty('--windowWidth', `${windowWidthUpdate}px`);
                });
                window.addEventListener('scroll', () => {
                    setAndroidBannerViewHeight(Math.max((87 - window.pageYOffset), 0));
                });
            }

            if (isPageScroll === true) {
                MsDyn365.msdyn365Commerce.isBrowser && document.documentElement.style.setProperty('--topBarSize', `0px`);
                MsDyn365.msdyn365Commerce.isBrowser && document.documentElement.style.setProperty('--storeLocatorSize', `0px`);
            }

            const _topBarSize = () => {
                const mobileTopBarSize =
                    (MsDyn365.msdyn365Commerce.isBrowser &&
                        document
                            .getElementsByClassName('ms-header__topbar')
                            .item(0)
                            ?.getBoundingClientRect().bottom) ||
                    0;
                {
                    mobileTopBarSize > 0 && setTopBarVisibleHeight(mobileTopBarSize);
                }
            };

            const _storeLocatorSize = () => {
                const storeLocatorSize =
                    (MsDyn365.msdyn365Commerce.isBrowser &&
                        document
                            .getElementsByClassName('ms-header__preferred-store-container')
                            .item(0)
                            ?.getBoundingClientRect().bottom) ||
                    0;
                {
                    storeLocatorSize > 0 && setVisibleHeight(storeLocatorSize);
                }
            };

            React.useEffect(() => {
                _topBarSize();
                _storeLocatorSize();
                MsDyn365.msdyn365Commerce.isBrowser &&
                    window.addEventListener('scroll', () => {
                        _mainMenuScroll(scrollValue, isPageScroll, setIsPageScroll, isOpen);
                        _topBarSize();
                        _storeLocatorSize();
                    });
                return () => {
                    MsDyn365.msdyn365Commerce.isBrowser &&
                        window.removeEventListener('scroll', () => {
                            _mainMenuScroll(scrollValue, isPageScroll, setIsPageScroll, isOpen);
                            _topBarSize();
                            _storeLocatorSize();
                        });
                };
            });

            React.useEffect(() => {
                if (props.data.navMenuState.result) {
                    props.context.actionContext.update(new NavMenuStateInput(), { isMobileMenuCollapsed: props.mobileMenuCollapsed });
                }
            }, [props.mobileMenuCollapsed]);
        };

        if (MsDyn365.msdyn365Commerce.isBrowser) {
            stickyNavigation();
        }

        const toggleModal = () => {
            setIsOpen(prevState => !prevState);
            MsDyn365.msdyn365Commerce.isBrowser && document.body.classList.toggle('header-fixed');
        };

        {
            /* VSI-Customization - End */
        }

        {
            /** VSI Customization - Start - 04/02/2021 */
        }
        {
            /** Checkout page header customization */
        }
        if (props.config.headerType && props.config.headerType === 'checkout') {
            const phoneNumber = props.config.phoneNumberCheckoutPage ? `tel:+${props.config.phoneNumberCheckoutPage}` : '';
            return (
                // @ts-ignore
                <Module {...updatedHeaderTag}>
                    <Node className={classnames(isHeaderSticky ? 'ms-header--fixed header-checkout' : 'ms-header__container')}>
                        <Node className={classnames('ms-header__checkout', isHeaderSticky ? 'ms-header__checkout--fixed' : '')}>
                            {props.logo}
                            {props.config.phoneNumberCheckoutPage ?
                                <a className='ms-header__checkout__phone-no' href={phoneNumber}><span>{props.config.phoneNumberCheckoutPage}</span></a>
                                : ''
                            }
                            {mfrmCartIcon}
                        </Node>
                    </Node>
                </Module>
            );
        }
        {
            /** VSI Customization - End - 04/02/2021 */
        }
        // tslint:disable-next-line: cyclomatic-complexity
        return (
            // @ts-ignore
            <Module {...updatedHeaderTag}>
                <Node {...HeaderContainer} className={isHeaderSticky ? 'ms-header--fixed' : 'ms-header__container'}>
                    <Node className='ms-header__container__wrapper ms-header__container__wrapper--lock'>
                        <div className='ms-header__preferred-store-container ms-header__preferred-store-container--mobile'>
                            {storeSelector}
                            <a className="ms-header__topbar__link ms-header__topbar__link-mobile fw-700"
                                title="(877) 348-7359"
                                href="tel:+18773487359"
                                aria-label="(877) 384-2903"
                                onClick={(event) => {
                                    // @ts-ignore
                                    if ('utag' in window) {
                                        // @ts-ignore
                                        // tslint:disable-next-line: object-literal-key-quotes
                                        utag.link({
                                            event: 'call-engagement',
                                            event_type: 'call',
                                            event_category: 'header click',
                                            event_action: 'call engagement',
                                            event_label: 'call_engagement'
                                        });
                                    }
                                }}
                            >
                                (877) 384-2903
                            </a>
                        </div>
                        {/* VSI-Customization - Start - Added MFRM Notifications slot  */}
                        {mfrmNotfications}
                        {/* VSI-Customization - End */}
                        {/* VSI-Customization - Start - Utility secondary logo and utility links */}
                        <Node {...HeaderTopBarContainer}>
                            <div className='ms-header__container-logo'>
                                <Logo
                                    {...{
                                        link: props.config.logoLink,
                                        image: props.config.logoImage,
                                        className: 'ms-header__logo',
                                        gridSettings: props.context.request.gridSettings,
                                        requestContext: props.context.request,
                                        telemetryContent: telemetryContent
                                    }}
                                />
                                <Logo
                                    {...{
                                        link: secondaryLogoLink,
                                        image: secondaryLogoImage,
                                        className: 'ms-header__logo__secondary',
                                        gridSettings: props.context.request.gridSettings,
                                        requestContext: props.context.request
                                    }}
                                />
                            </div>
                            <div className='ms-header__topbar__links'>
                                <div className='ms-header__topbar__links--desktop'>
                                    {desktopUtilityLinks &&
                                        desktopUtilityLinks.length > 0 &&
                                        desktopUtilityLinks.map((link: IUtilityLinksData, index: number) => (
                                            <a
                                                key={index}
                                                className={classnames('ms-header__topbar__link', link.className)}
                                                title={link.linkText}
                                                href={navLinkKeywordRemoval(
                                                    (link.linkUrl && link.linkUrl.destinationUrl) || '',
                                                    props.context.request
                                                )}
                                                aria-label={link.ariaLabel}
                                                target={link.openInNewTab ? '_blank' : undefined}
                                                // tslint:disable-next-line: jsx-no-lambda
                                                onClick={event => {
                                                    const isChat = link.ariaLabel === 'Chat' ? true : false;

                                                    if (isChat) {
                                                        event.preventDefault();
                                                        // tslint:disable: no-function-expression
                                                        // tslint:disable: typedef
                                                        setTimeout(function () {
                                                            // eslint-disable-next-line no-eval
                                                            eval(
                                                                `var elems = document.querySelectorAll('.uiButton.helpButtonEnabled, .minimizedContainer.helpButton'); if(elems != undefined && elems.length > 0) { if(elems.length > 1){elems[1].click()} else {elems[0].click()}}`
                                                            );
                                                            // eslint-disable-next-line no-eval
                                                            // eval(
                                                            //     `document.querySelectorAll('.embeddedServiceHelpButton')[0].classList.add('show-chat');`
                                                            // );
                                                        }, 1000);
                                                        // @ts-ignore
                                                        if (utag) {
                                                            // @ts-ignore
                                                            // tslint:disable-next-line: object-literal-key-quotes
                                                            utag.link({
                                                                event: 'chat-widget-initiated',
                                                                event_type: 'chat_click',
                                                                event_category: 'chat',
                                                                event_action: 'click',
                                                                event_label: 'chat engagement'
                                                            });
                                                        }
                                                    }

                                                    // @ts-ignore
                                                    if (utag) {
                                                        event.preventDefault();
                                                        const isExternal = isExternalLink(link.linkUrl.destinationUrl);
                                                        const isPhoneNum = link.linkUrl.destinationUrl.startsWith('tel') ? true : false;
                                                        let eventName = isExternal ? 'outbound-general-link-click' : 'internal-navigation';
                                                        let eventCategory = isExternal ? 'outbound links' : 'internal links';
                                                        let eventAction = '';
                                                        let eventLabel = `from header: ${MsDyn365.msdyn365Commerce.isBrowser &&
                                                            window.location.href}`;
                                                        let eventType = '';

                                                        if (isPhoneNum) {
                                                            eventName = 'call-engagement';
                                                            eventCategory = 'call';
                                                            eventAction = 'header click';
                                                            eventLabel = 'call engagement';
                                                            eventType = 'call_engagement';
                                                        } else if (isChat) {
                                                            eventName = 'chat-widget-initiated';
                                                            eventType = 'chat_click';
                                                            eventCategory = 'chat';
                                                            eventAction = 'click';
                                                            eventLabel = 'chat engagement';
                                                        } else {
                                                            eventAction = `to: ${link.linkUrl.destinationUrl}`;
                                                        }
                                                        // @ts-ignore
                                                        // tslint:disable-next-line: object-literal-key-quotes
                                                        utag.link({
                                                            event: eventName,
                                                            event_type: eventType,
                                                            event_category: eventCategory,
                                                            event_action: eventAction,
                                                            event_label: eventLabel,
                                                            event_noninteraction: 'false'
                                                        });
                                                        if (MsDyn365.msdyn365Commerce.isBrowser && !isChat === true) {
                                                            const isOpenInNewTab = link.openInNewTab;
                                                            !isOpenInNewTab
                                                                ? (window.location.href = link.linkUrl.destinationUrl)
                                                                : window.open(link.linkUrl.destinationUrl);
                                                        }
                                                    }
                                                }}
                                            >
                                                {link.linkText}
                                            </a>
                                        ))}
                                </div>
                                <div className='ms-header__topbar__links--tab'>
                                    {tabUtilityLinks &&
                                        tabUtilityLinks.length > 0 &&
                                        tabUtilityLinks.map((link: IUtilityLinksData, index: number) => (
                                            <a
                                                key={index}
                                                className={classnames('ms-header__topbar__link', link.className)}
                                                title={link.linkText}
                                                href={navLinkKeywordRemoval(
                                                    (link.linkUrl && link.linkUrl.destinationUrl) || '',
                                                    props.context.request
                                                )}
                                                aria-label={link.ariaLabel}
                                                target={link.openInNewTab ? '_blank' : undefined}
                                                // tslint:disable-next-line: jsx-no-lambda
                                                onClick={event => {
                                                    const isChat = link.ariaLabel === 'Chat' ? true : false;
                                                    const isPhoneNum = link.linkUrl.destinationUrl.startsWith('tel') ? true : false;
                                                    const isExternal = isExternalLink(link.linkUrl.destinationUrl);
                                                    let eventName = isExternal ? 'outbound-general-link-click' : 'internal-navigation';
                                                    let eventCategory = isExternal ? 'outbound links' : 'internal links';
                                                    let eventAction = `to: ${link.linkUrl.destinationUrl}`;
                                                    let eventLabel = `from header: ${MsDyn365.msdyn365Commerce.isBrowser && window.location.href}`;
                                                    let eventType = '';

                                                    if (isChat) {
                                                        event.preventDefault();
                                                        // tslint:disable: no-function-expression
                                                        // tslint:disable: typedef
                                                        setTimeout(function () {
                                                            // eslint-disable-next-line no-eval
                                                            eval(
                                                                `var elems = document.querySelectorAll('.uiButton.helpButtonEnabled, .minimizedContainer.helpButton, .sidebarHeader.minimizedContainer'); if(elems != undefined && elems.length > 0) { if(elems.length > 1){elems[1].click()} else {elems[0].click()}}`
                                                            );
                                                        }, 1000);
                                                    } else if (isPhoneNum) {
                                                        eventName = 'call-engagement';
                                                        eventCategory = 'call';
                                                        eventAction = 'header click';
                                                        eventLabel = 'call engagement';
                                                        eventType = 'call_engagement';
                                                    }
                                                    // @ts-ignore
                                                    if (utag) {
                                                        event.preventDefault();
                                                        // @ts-ignore
                                                        // tslint:disable-next-line: object-literal-key-quotes
                                                        utag.link({
                                                            event: eventName,
                                                            event_type: eventType,
                                                            event_category: eventCategory,
                                                            event_action: eventAction,
                                                            event_label: eventLabel,
                                                            event_noninteraction: 'false'
                                                        });
                                                        if (MsDyn365.msdyn365Commerce.isBrowser && !isChat === true) {
                                                            const isOpenInNewTab = link.openInNewTab;
                                                            !isOpenInNewTab
                                                                ? (window.location.href = link.linkUrl.destinationUrl)
                                                                : window.open(link.linkUrl.destinationUrl);
                                                        }
                                                    }
                                                }}
                                            >
                                                {link.linkText}
                                            </a>
                                        ))}
                                </div>
                            </div>
                        </Node>
                        {/* VSI-Customization - End - Utility secondary logo and utility links */}
                        <div className='ms-header__primary__container'>
                            <div className={classnames('ms-header__primary', isPageScroll && isHeaderSticky ? 'ms-header__primary--fixed' : '')}>
                                <div className='ms-header__primary__wrapper'>
                                    {props.navIcon}
                                    {<div className='ms-header__primary__nav'>{_renderReactFragment(mfrmMenuBar)}</div>}
                                    {_renderReactFragment(mfrmSearch)}
                                    <div className='ms-header__primary__links'>
                                        {!isMobile && (
                                            <span role='button' className='ms-header__signin-button' aria-label='Sign in Button' onClick={toggleModal} tabIndex={0}>
                                                <span
                                                    className={classnames(
                                                        isOpen ? 'ms-header__signin-button--opened' : 'ms-header__signin-button--closed'
                                                    )}
                                                />
                                            </span>
                                        )}
                                        {mfrmCartIcon}
                                    </div>
                                    <Modal
                                        autoFocus={true}
                                        fade={false}
                                        isOpen={isOpen}
                                        horizontalPosition={'center'}
                                        verticalPosition={'center'}
                                        zIndex={5003}
                                        toggle={toggleModal}
                                        applicationNode={'rsg-root'}
                                        modalClassName={classnames('account-dialog')}
                                    >
                                        <ModalHeader toggle={toggleModal} />
                                        <ModalBody>
                                            <div className='ms-header__primary__links'>{_renderDesktopAccountBlock(props)}</div>
                                        </ModalBody>
                                    </Modal>
                                </div>
                                {/* Mobile Container starts here */}
                                <Node
                                    {...{
                                        className: classnames(
                                            'ms-header__mobile-hamburger-menu',
                                            props.mobileMenuCollapsed
                                                ? 'ms-header__mobile-hamburger-menu--hidden'
                                                : 'ms-header__mobile-hamburger-menu--show'
                                        )
                                    }}
                                >
                                    <Node {...{ className: 'ms-header__mobile-hamburger-menu-body' }}>
                                        <Node
                                            {...MobileMenuLinksContainer}
                                            className={
                                                navMenuParentActive
                                                    ? 'ms-header__mobile-hamburger-menu-links hidden'
                                                    : 'ms-header__mobile-hamburger-menu-links'
                                            }
                                        >
                                            {props.data.accountInformation && props.data.accountInformation.result && (
                                                <h2>{props.resources.accountHeading}</h2>
                                            )}
                                            {props.accountLinks &&
                                                props.accountLinks[0] &&
                                                props.accountLinks.filter((link, index) => index === 0)[0]}
                                            {_signInButton(customer, props.context.request.user, props)}
                                            {props.signOutLink}
                                            {!props.accountLinks && _signUpButton(customer, props.context.request.user, props)}
                                            {/* Todo: Placeholder Create account Link - END */}
                                        </Node>
                                        {_renderReactFragment(mfrmMenuBar)}
                                        <ul
                                            className={
                                                navMenuParentActive ? 'ms-header__topbar__links--mobile hidden' : 'ms-header__topbar__links--mobile'
                                            }
                                        >
                                            {hamburgerUtilityLinks &&
                                                hamburgerUtilityLinks.length > 0 &&
                                                hamburgerUtilityLinks.map((link: IUtilityLinksData, index: number) => {
                                                    const navUrl = navLinkKeywordRemoval(
                                                        (link.linkUrl && link.linkUrl.destinationUrl) || '',
                                                        props.context.request
                                                    );
                                                    return (
                                                        <li key={index}>
                                                            <a
                                                                className={classnames('ms-header__topbar__link', link.className)}
                                                                title={link.linkText}
                                                                href={navUrl}
                                                                aria-label={link.ariaLabel}
                                                                target={link.openInNewTab ? '_blank' : undefined}
                                                                // tslint:disable-next-line: jsx-no-lambda
                                                                onClick={event => {
                                                                    const isChat = link.ariaLabel === 'Chat' ? true : false;

                                                                    if (isChat) {
                                                                        event.preventDefault();
                                                                        // tslint:disable: no-function-expression
                                                                        // tslint:disable: typedef
                                                                        setTimeout(function () {
                                                                            // eslint-disable-next-line no-eval
                                                                            eval(
                                                                                `var elems = document.querySelectorAll('.uiButton.helpButtonEnabled, .minimizedContainer.helpButton, .sidebarHeader.minimizedContainer'); if(elems != undefined && elems.length > 0) { if(elems.length > 1){elems[1].click()} else {elems[0].click()}}`
                                                                            );
                                                                        }, 1000);
                                                                    }

                                                                    // @ts-ignore
                                                                    if (utag) {
                                                                        event.preventDefault();
                                                                        const isExternal = isExternalLink(link.linkUrl.destinationUrl);
                                                                        const isPhoneNum = link.linkUrl.destinationUrl.startsWith('tel')
                                                                            ? true
                                                                            : false;
                                                                        let eventName = isExternal
                                                                            ? 'outbound-general-link-click'
                                                                            : 'internal-navigation';
                                                                        let eventCategory = isExternal ? 'outbound links' : 'internal links';
                                                                        let eventAction = `to: ${navUrl}`;
                                                                        let eventLabel = `from header: ${MsDyn365.msdyn365Commerce.isBrowser &&
                                                                            window.location.href}`;
                                                                        let eventType = '';

                                                                        if (isPhoneNum) {
                                                                            eventName = 'call-engagement';
                                                                            eventCategory = 'call';
                                                                            eventAction = 'header click';
                                                                            eventLabel = 'call engagement';
                                                                            eventType = 'call_engagement';
                                                                        }
                                                                        // @ts-ignore
                                                                        // tslint:disable-next-line: object-literal-key-quotes max-line-length
                                                                        utag.link({
                                                                            event: eventName,
                                                                            event_type: eventType,
                                                                            event_category: eventCategory,
                                                                            event_action: eventAction,
                                                                            event_label: eventLabel,
                                                                            event_noninteraction: 'false'
                                                                        });
                                                                        if (MsDyn365.msdyn365Commerce.isBrowser && !isChat === true) {
                                                                            const isOpenInNewTab = link.openInNewTab;
                                                                            !isOpenInNewTab ? (window.location.href = navUrl) : window.open(navUrl);
                                                                        }
                                                                    }
                                                                }}
                                                            >
                                                                {link.linkText}
                                                            </a>
                                                        </li>
                                                    );
                                                })}
                                        </ul>
                                        <ul
                                            className={
                                                navMenuParentActive ? 'ms-header__topbar__links--tab hidden' : 'ms-header__topbar__links--tab'
                                            }
                                        >
                                            {hamburgerTabUtilityLinks &&
                                                hamburgerTabUtilityLinks.length > 0 &&
                                                hamburgerTabUtilityLinks.map((link: IUtilityLinksData, index: number) => {
                                                    const navUrl = navLinkKeywordRemoval(
                                                        (link.linkUrl && link.linkUrl.destinationUrl) || '',
                                                        props.context.request
                                                    );
                                                    return (
                                                        <li key={index}>
                                                            <a
                                                                className={classnames('ms-header__topbar__link', link.className)}
                                                                title={link.linkText}
                                                                href={navUrl}
                                                                aria-label={link.ariaLabel}
                                                                target={link.openInNewTab ? '_blank' : undefined}
                                                                // tslint:disable-next-line: jsx-no-lambda
                                                                onClick={event => {
                                                                    const isChat = link.ariaLabel === 'Chat' ? true : false;
                                                                    const isExternal = isExternalLink(link.linkUrl.destinationUrl);
                                                                    const isPhoneNum = link.linkUrl.destinationUrl.startsWith('tel') ? true : false;
                                                                    let eventName = isExternal
                                                                        ? 'outbound-general-link-click'
                                                                        : 'internal-navigation';
                                                                    let eventCategory = isExternal ? 'outbound links' : 'internal links';
                                                                    let eventAction = `to: ${navUrl}`;
                                                                    let eventLabel = `from header: ${MsDyn365.msdyn365Commerce.isBrowser &&
                                                                        window.location.href}`;
                                                                    let eventType = '';

                                                                    if (isChat) {
                                                                        event.preventDefault();
                                                                        // tslint:disable: no-function-expression
                                                                        // tslint:disable: typedef
                                                                        setTimeout(function () {
                                                                            // eslint-disable-next-line no-eval
                                                                            eval(
                                                                                `var elems = document.querySelectorAll('.uiButton.helpButtonEnabled, .minimizedContainer.helpButton, .sidebarHeader.minimizedContainer'); if(elems != undefined && elems.length > 0) { if(elems.length > 1){elems[1].click()} else {elems[0].click()}}`
                                                                            );
                                                                        }, 1000);
                                                                    } else if (isPhoneNum) {
                                                                        eventName = 'call-engagement';
                                                                        eventCategory = 'call';
                                                                        eventAction = 'header click';
                                                                        eventLabel = 'call engagement';
                                                                        eventType = 'call_engagement';
                                                                    }
                                                                    // @ts-ignore
                                                                    if (utag) {
                                                                        event.preventDefault();
                                                                        // @ts-ignore
                                                                        // tslint:disable-next-line: object-literal-key-quotes max-line-length
                                                                        utag.link({
                                                                            event: eventName,
                                                                            event_type: eventType,
                                                                            event_category: eventCategory,
                                                                            event_action: eventAction,
                                                                            event_label: eventLabel,
                                                                            event_noninteraction: 'false'
                                                                        });
                                                                        if (MsDyn365.msdyn365Commerce.isBrowser && !isChat === true) {
                                                                            const isOpenInNewTab = link.openInNewTab;
                                                                            !isOpenInNewTab ? (window.location.href = navUrl) : window.open(navUrl);
                                                                        }
                                                                    }
                                                                }}
                                                            >
                                                                {link.linkText}
                                                            </a>
                                                        </li>
                                                    );
                                                })}
                                        </ul>
                                    </Node>
                                    <div className='ms-header__mobile-hamburger-menu__backdrop' />
                                </Node>
                            </div>
                        </div>
                        {/* VSI-Customization - End - Primary Navigation */}
                    </Node>
                    <div className='ms-header__secondary ms-header__secondary--lock'>
                        <div className='ms-header__preferred-store-container ms-header__preferred-store-container--desktop'>
                            {isMobile ? null : storeSelector}
                        </div>
                        <div className='ms-header__secondary__nav'>
                            {secondaryNavItems &&
                                secondaryNavItems.length > 0 &&
                                secondaryNavItems.slice(0, secondaryNavLimit).map((link: ISecondaryNavItemsData, index: number) => {
                                    const navUrl = navLinkKeywordRemoval(
                                        (link.linkUrl && link.linkUrl.destinationUrl) || '',
                                        props.context.request
                                    );
                                    return (
                                        <a
                                            key={index}
                                            className={classnames('ms-header__secondary__nav__link')}
                                            title={link.linkText}
                                            href={navUrl}
                                            aria-label={link.ariaLabel}
                                            target={link.openInNewTab ? '_blank' : undefined}
                                            // tslint:disable-next-line: jsx-no-lambda
                                            onClick={event => {
                                                // @ts-ignore
                                                if (utag) {
                                                    event.preventDefault();
                                                    const isExternal = isExternalLink(link.linkUrl.destinationUrl);
                                                    const eventName = isExternal ? 'outbound-general-link-click' : 'internal-navigation';
                                                    const eventCategory = isExternal ? 'outbound links' : 'internal links';
                                                    // @ts-ignore
                                                    // tslint:disable-next-line: object-literal-key-quotes
                                                    utag.link({
                                                        event: eventName,
                                                        event_category: eventCategory,
                                                        event_action: `to: ${navUrl}`,
                                                        event_label: `from header: ${MsDyn365.msdyn365Commerce.isBrowser &&
                                                            window.location.href}`,
                                                        event_noninteraction: 'false'
                                                    });
                                                    if (MsDyn365.msdyn365Commerce.isBrowser) {
                                                        const isOpenInNewTab = link.openInNewTab;
                                                        !isOpenInNewTab ? (window.location.href = navUrl) : window.open(navUrl);
                                                    }
                                                }
                                            }}
                                        >
                                            {link.linkText}
                                        </a>
                                    );
                                })}
                        </div>
                    </div>
                    {/* VSI-Customization - End - Secondary Navigation */}
                </Node>
            </Module>
        );
    };

function _renderDesktopAccountBlock(props: IHeaderViewProps & IHeaderProps<IMfrmExtendedNavMenuData>): JSX.Element | null {
    const { AccountInfoDropdownParentContainer, signOutLink, accountLinks } = props;
    const customer = props.data.accountInformation?.result;
    const signInLink = _signInButton(customer, props.context.request.user, props);
    const signUpLink = _signUpButton(customer, props.context.request.user, props);
    if (AccountInfoDropdownParentContainer) {
        if (customer) {
            return (
                <Node className='ms-header__account-links'>
                    {signOutLink}
                    {accountLinks ? accountLinks.map(link => link) : false}
                </Node>
            );
        } else if (signInLink) {
            return (
                <Node {...AccountInfoDropdownParentContainer}>
                    {signInLink}
                    {signUpLink}
                    {/* Todo: create a method to render sign up link  and add here*/}
                </Node>
            );
        }
    }
    props.context.telemetry.warning('Header content is empty, module wont render.');
    return null;
}

function _renderReactFragment(items: React.ReactNode[]): JSX.Element | null {
    return (
        <React.Fragment>
            {items && items.length
                ? items.map((slot: React.ReactNode, index: number) => {
                    return <React.Fragment key={index}>{slot}</React.Fragment>;
                })
                : null}
        </React.Fragment>
    );
}

// VSI CUSTOMIZATION - starts here
function _mainMenuScroll(
    pageScrollValue: number,
    isPageScroll: boolean,
    setIsPageScroll: React.Dispatch<React.SetStateAction<boolean>>,
    isOpen: boolean
): void {
    if (
        MsDyn365.msdyn365Commerce.isBrowser &&
        (window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop) >= pageScrollValue
    ) {
        if (isPageScroll !== true) {
            setIsPageScroll(true);
        }
    } else {
        if (isOpen === false) {
            setIsPageScroll(false);
        } else if (isOpen === true) {
            setIsPageScroll(true);
        }
    }
}

function _signUpButton(
    customer: Customer | undefined,
    user: MsDyn365.IRequestContextUser | undefined,
    props: IHeaderViewProps & IHeaderProps<IMfrmExtendedNavMenuData>
): React.ReactNode | undefined {
    const telemetryContent = getTelemetryObject(props.context.request.telemetryPageName!, props.friendlyName, props.telemetry);
    const payLoad = getPayloadObject('click', telemetryContent, TelemetryConstant.SignUp);
    const attributes = getTelemetryAttributes(telemetryContent, payLoad);
    const signupUrl = modifiedAuthUrl(user?.signUpUrl);
    return (
        !customer && (
            <Button
                className='ms-header__signup-button'
                title={props.resources.signUpLinkText}
                href={signupUrl}
                aria-label={props.resources.signUpLinkAriaText}
                onClick={onTelemetryClick(telemetryContent, payLoad, 'Sign up')}
                {...attributes}
            >
                <span className='ms-header__signup-button-text' aria-hidden={true}>
                    {props.resources.signUpLinkText}
                </span>
            </Button>
        )
    );
}
function _signInButton(
    customer: Customer | undefined,
    user: MsDyn365.IRequestContextUser | undefined,
    props: IHeaderViewProps & IHeaderProps<IMfrmExtendedNavMenuData>
): React.ReactNode | undefined {
    const telemetryContent = getTelemetryObject(props.context.request.telemetryPageName!, props.friendlyName, props.telemetry);
    const payLoad = getPayloadObject('click', telemetryContent, TelemetryConstant.SignIn);
    const attributes = getTelemetryAttributes(telemetryContent, payLoad);
    const signinUrl = modifiedAuthUrl(user?.signInUrl);

    return !customer && (
        <Button
            className='ms-header__signin-button'
            title={props.resources.signInLinkText}
            href={signinUrl}
            aria-label={props.resources.signInLinkAriaText}
            onClick={onTelemetryClick(telemetryContent, payLoad, 'Sign in')}
            {...attributes}
        >
            <span className='ms-header__signin-button-text' aria-hidden>
                {props.resources.signInLinkText}
            </span>
        </Button>
    );
}

const modifiedAuthUrl = (url: string | undefined): string | undefined => {
    const urlParts = url?.split('\/');
    urlParts?.splice(3, 1);
    const newUrl = urlParts?.join('\/');
    return newUrl;

};

// Method for getting and updating viewport
const updateViewPort = (
    context: MsDyn365.ICoreContext,
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>,
    setIsMobile: React.Dispatch<React.SetStateAction<boolean>>,
    setIsTablet: React.Dispatch<React.SetStateAction<boolean>>
): void => {
    const viewport = _getViewPort(context);

    if (viewport === 'xs' || viewport === 'sm') {
        setIsMobile(true);
        setIsTablet(false);
        setIsOpen(false);
    } else if (viewport === 'md' || viewport === 'lg') {
        setIsMobile(false);
        setIsTablet(true);
    } else {
        setIsMobile(false);
        setIsTablet(false);
    }
};
// VSI CUSTOMIZATION - ends here

export default headerView;
